import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Code2, Eraser
} from 'lucide-react';
import { markdownToHtml } from '../utils/markdownToHtml';
import { isFullDocument } from './RichContent';

const exec = (command, value = null) => document.execCommand(command, false, value);

const TOOLS = [
  { icon: Bold, title: 'Gras', run: () => exec('bold') },
  { icon: Italic, title: 'Italique', run: () => exec('italic') },
  { icon: Heading2, title: 'Titre', run: () => exec('formatBlock', '<h2>') },
  { icon: Heading3, title: 'Sous-titre', run: () => exec('formatBlock', '<h3>') },
  { icon: List, title: 'Liste à puces', run: () => exec('insertUnorderedList') },
  { icon: ListOrdered, title: 'Liste numérotée', run: () => exec('insertOrderedList') },
  { icon: Quote, title: 'Encadré', run: () => exec('formatBlock', '<blockquote>') },
  {
    icon: Link2,
    title: 'Lien',
    run: () => {
      const url = window.prompt('Adresse du lien (https://...)');
      if (url) exec('createLink', url);
    },
  },
  { icon: Eraser, title: 'Nettoyer le format', run: () => exec('removeFormat') },
];

/** Visual rich-text editor (contentEditable) with a raw HTML fallback tab. */
export const RichTextEditor = ({ value = '', onChange, rows = 12, placeholder = '', testId = 'rich-text-editor' }) => {
  const [htmlMode, setHtmlMode] = useState(false);
  const editorRef = useRef(null);
  // Un document complet (CSS/SVG) ne passe pas dans l'éditeur visuel : mode code forcé
  const fullDoc = isFullDocument(value);
  const codeMode = htmlMode || fullDoc;

  const emit = (html) => onChange?.((html || '').replace(/&nbsp;/g, ' '));

  useEffect(() => {
    const editor = editorRef.current;
    if (codeMode || !editor) return;
    // Never rewrite while the author is typing: it would destroy the caret position
    if (document.activeElement === editor) return;
    // Legacy markdown content: convert so the visual editor never shows raw syntax
    const isHtml = /<[a-z][\s\S]*>/i.test(value || '');
    const desired = value ? (isHtml ? value : markdownToHtml(value)) : '';
    if (editor.innerHTML !== desired) editor.innerHTML = desired;
  }, [value, codeMode]);

  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-secondary/40">
        {!codeMode && TOOLS.map(({ icon: Icon, title, run }) => (
          <Button
            key={title}
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title={title}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              run();
              emit(editorRef.current?.innerHTML);
            }}
            data-testid={`rte-${title.toLowerCase().replace(/\s|à|é/g, '-')}`}
          >
            <Icon className="w-4 h-4" />
          </Button>
        ))}
        {fullDoc ? (
          <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="rte-fulldoc-badge">
            <Code2 className="w-4 h-4" />
            Document complet (CSS conservé)
          </span>
        ) : (
          <Button
            type="button"
            variant={htmlMode ? 'default' : 'ghost'}
            size="sm"
            className="h-8 ml-auto text-xs"
            onClick={() => setHtmlMode(!htmlMode)}
            data-testid="rte-toggle-html"
          >
            <Code2 className="w-4 h-4 mr-1" />
            {htmlMode ? 'Retour au visuel' : 'HTML'}
          </Button>
        )}
      </div>

      {codeMode ? (
        <Textarea
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          rows={rows}
          className="border-0 rounded-none font-mono text-xs focus-visible:ring-0"
          placeholder="<h2>Titre</h2><p>Ton texte…</p>"
          data-testid="rte-html-input"
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => emit(e.currentTarget.innerHTML)}
          onBlur={(e) => emit(e.currentTarget.innerHTML)}
          className="rich-content min-h-[200px] p-4 outline-none focus:ring-0"
          style={{ minHeight: `${rows * 22}px` }}
          data-placeholder={placeholder}
          data-testid="rte-visual-input"
        />
      )}
    </div>
  );
};

export default RichTextEditor;
